import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { furnitureGroupService } from '@/services/FurnitureGroupService'
import { furnitureGroupKeys } from '@/hooks/queryKeys'
import type { FurnitureGroupDto, FurnitureGroupListDto } from '@lib/dto/furniture-group.dto'
import type { CreateFurnitureGroupInput, UpdateFurnitureGroupInput } from '@lib/input/furniture-group.input'

/** A venue's furniture arrangements (the API scopes to the caller's venues). */
export function useFurnitureGroups(
  venueId?: string | null,
  options: { enabled?: boolean } = {},
): UseQueryResult<FurnitureGroupListDto> {
  return useQuery({
    // The venue is part of the key: without it, switching venue would show the
    // previous one's arrangements from cache.
    queryKey: furnitureGroupKeys.list(venueId),
    queryFn: () => furnitureGroupService.list(venueId),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  })
}

/** Create a furniture group, refreshing the list on success. */
export function useCreateFurnitureGroup(): UseMutationResult<FurnitureGroupDto, Error, CreateFurnitureGroupInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateFurnitureGroupInput) => furnitureGroupService.create(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: furnitureGroupKeys.lists() }),
  })
}

/** Update a furniture group, refreshing the list on success. */
export function useUpdateFurnitureGroup(): UseMutationResult<
  FurnitureGroupDto,
  Error,
  { id: string; input: UpdateFurnitureGroupInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateFurnitureGroupInput }) =>
      furnitureGroupService.update(vars.id, vars.input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: furnitureGroupKeys.lists() }),
  })
}
